import { Search, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/v2/accordion";
import { Badge } from "@/components/ui/v2/badge";
import { Button } from "@/components/ui/v2/button";
import { Checkbox } from "@/components/ui/v2/checkbox";
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/v2/drawer";
import { Label } from "@/components/ui/v2/label";
import SearchInput from "@/components/ui/v2/search-input";
import { Separator } from "@/components/ui/v2/separator";
import { Skeleton } from "@/components/ui/v2/skeleton";
import { URL_PARAMS } from "@/constants/urlParams";
import { useVacancyCount } from "@/features/vacancies/api/useVacancyCount";
import { useVacancyFilters } from "@/features/vacancies/api/useVacancyFilters";
import { VacancyFilters } from "@/lib/contexts/Vacancy/types";
import { FilterOption } from "@/types/FilterOption";

type VacanciesFiltersDrawerProps = Readonly<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  searchParams: URLSearchParams;
  clientId?: string;
  includeUnpublished?: boolean;
  searchValue?: string;
}>;

type FilterSectionProps = {
  value: string;
  title: string;
  options: FilterOption<string>[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
};

function FilterSection({
  value,
  title,
  options,
  selectedValues,
  onSelectionChange,
}: FilterSectionProps) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");

  const handleToggle = (optionValue: string) => {
    if (selectedValues.includes(optionValue)) {
      onSelectionChange(selectedValues.filter((v) => v !== optionValue));
    } else {
      onSelectionChange([...selectedValues, optionValue]);
    }
  };

  const showSearch = options.length > 5;
  const filteredOptions = showSearch
    ? options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    : options;

  return (
    <AccordionItem value={value} className="border rounded-lg last:border-b">
      <AccordionTrigger className="px-4">{t(title)}</AccordionTrigger>
      <AccordionContent className="px-4">
        <div className="space-y-2">
          {showSearch && (
            <div className="pb-2">
              <SearchInput
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t("common.search")}
              />
            </div>
          )}
          <div className="max-h-64 overflow-y-auto overflow-x-hidden -mx-2 px-2">
            {filteredOptions.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">
                {t("commonSentences.noOptionsFound")}
              </p>
            ) : (
              filteredOptions.map((option) => (
                <Label
                  key={option.value}
                  className="flex items-center gap-2 cursor-pointer hover-tint-primary transition-colors rounded-md p-2 -mx-2"
                >
                  <Checkbox
                    checked={selectedValues.includes(option.value)}
                    onCheckedChange={() => handleToggle(option.value)}
                  />
                  <span className="text-sm flex-1">{option.label}</span>
                  {option.count !== undefined && (
                    <span className="text-xs text-muted-foreground">
                      ({option.count})
                    </span>
                  )}
                </Label>
              ))
            )}
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

export function VacanciesFiltersDrawer({
  open,
  onOpenChange,
  searchParams,
  clientId,
  includeUnpublished,
  searchValue,
}: VacanciesFiltersDrawerProps) {
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParams();

  const getFiltersFromParams = useCallback((): VacancyFilters => {
    return {
      city: searchParams.getAll("city"),
      country: searchParams.getAll("country"),
      department: searchParams.getAll("department"),
      field: searchParams.getAll("field"),
      channels: searchParams.getAll("channels"),
      publicationChannels: searchParams.getAll("publicationChannels"),
      company: searchParams.getAll("company"),
      district: searchParams.getAll("district"),
    };
  }, [searchParams]);

  const [currentFilters, setCurrentFilters] =
    useState<VacancyFilters>(getFiltersFromParams);

  useEffect(() => {
    setCurrentFilters(getFiltersFromParams());
  }, [searchParams, open, getFiltersFromParams]);

  const { data: filters, isLoading: filtersLoading } = useVacancyFilters({
    clientId,
    search: searchValue,
    includeUnpublished,
  });

  const { countByFilter, countByFilterLoading } = useVacancyCount({
    filter: currentFilters,
    search: searchValue,
    includeUnpublished,
    clientId,
  });

  const handleApplyFilters = () => {
    const newSearchParams = new URLSearchParams();

    Object.entries(currentFilters).forEach(([key, values]) => {
      if (Array.isArray(values) && values.length > 0) {
        values.forEach((value) => {
          newSearchParams.append(key, value);
        });
      }
    });

    newSearchParams.set(URL_PARAMS.PAGE, "1");
    if (searchValue) {
      newSearchParams.set(URL_PARAMS.SEARCH, searchValue);
    }

    // Preserve sort parameters if they exist
    const orderBy = searchParams.get(URL_PARAMS.ORDER_BY);
    const descending = searchParams.get(URL_PARAMS.DESCENDING);
    if (orderBy) newSearchParams.set(URL_PARAMS.ORDER_BY, orderBy);
    if (descending) newSearchParams.set(URL_PARAMS.DESCENDING, descending);

    // Preserve includeUnpublished if it exists
    if (includeUnpublished) {
      newSearchParams.set("showUnpublished", "true");
    }

    setSearchParams(newSearchParams);
    onOpenChange(false);
  };

  const handleClearFilters = () => {
    const defaultFilters: VacancyFilters = {
      city: [],
      country: [],
      department: [],
      field: [],
      channels: [],
      publicationChannels: [],
      company: [],
      district: [],
    };
    setCurrentFilters(defaultFilters);

    const newSearchParams = new URLSearchParams(searchParams);
    Object.keys(defaultFilters).forEach((key) => newSearchParams.delete(key));
    setSearchParams(newSearchParams);
  };

  const handleFilterChange = (key: keyof VacancyFilters, values: string[]) => {
    setCurrentFilters((prev) => ({
      ...prev,
      [key]: values,
    }));
  };

  const entityName = t("common.vacancies");

  // Build filter sections from available filters
  type FilterSection = {
    key: keyof VacancyFilters;
    title: string;
    options: FilterOption<string>[];
  };

  const filterSections: FilterSection[] = [];
  if (filters?.field) {
    filterSections.push({
      key: "field" as const,
      title: "common.field",
      options: filters.field,
    });
  }
  if (filters?.city) {
    filterSections.push({
      key: "city" as const,
      title: "common.city",
      options: filters.city,
    });
  }
  if (filters?.country) {
    filterSections.push({
      key: "country" as const,
      title: "common.country",
      options: filters.country,
    });
  }
  if (filters?.department) {
    filterSections.push({
      key: "department" as const,
      title: "common.department",
      options: filters.department,
    });
  }
  if (filters?.channels) {
    filterSections.push({
      key: "channels" as const,
      title: "common.channels",
      options: filters.channels,
    });
  }
  if (filters?.publicationChannels) {
    filterSections.push({
      key: "publicationChannels" as const,
      title: "common.publicationChannels",
      options: filters.publicationChannels,
    });
  }
  if (filters?.company) {
    filterSections.push({
      key: "company" as const,
      title: "common.company",
      options: filters.company,
    });
  }
  if (filters?.district) {
    filterSections.push({
      key: "district" as const,
      title: "common.district",
      options: filters.district,
    });
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>
            {t("admin.filters.filterTitle", { entity: entityName })}
          </DrawerTitle>
        </DrawerHeader>

        <div className="flex flex-col gap-4 overflow-y-auto px-4">
          {/* Total Records */}
          <div className="flex items-center justify-between rounded-lg border bg-muted/30 p-3">
            <span className="text-sm font-medium">
              {t("admin.filters.totalRecords", {
                count: countByFilter?.count ?? 0,
                entity: entityName,
              })}
            </span>
            {countByFilterLoading && <Skeleton className="h-4 w-16" />}
          </div>

          {/* Search Tag */}
          {searchValue && (
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="gap-1.5">
                <Search className="h-3 w-3" />
                <span className="text-xs">"{searchValue}"</span>
              </Badge>
            </div>
          )}

          <Separator />

          {/* Filter Sections */}
          {filtersLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : (
            <Accordion
              type="multiple"
              defaultValue={filterSections.map((section) => section.key)}
              className="w-full flex flex-col gap-4 pb-4"
            >
              {filterSections.map((section) => (
                <FilterSection
                  key={section.key}
                  value={section.key}
                  title={section.title}
                  options={section.options}
                  selectedValues={currentFilters[section.key] || []}
                  onSelectionChange={(values) =>
                    handleFilterChange(section.key, values)
                  }
                />
              ))}
            </Accordion>
          )}
        </div>

        <DrawerFooter className="w-full md:flex-row gap-2">
          <Button onClick={handleApplyFilters} className="md:w-1/2">
            {t("admin.filters.showFilterdData", { entity: entityName })}
          </Button>
          <Button
            variant="ghost"
            onClick={handleClearFilters}
            className="md:w-1/2"
          >
            <Trash2 className="h-4 w-4" />
            {t("admin.filters.resetAllFilters")}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
